import React, { FC, useContext, useEffect, useRef, useState } from 'react';

import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as Types from '@typings/graphql-models';
import * as ApiModels from '@typings/api-models';
import { useUser } from '@hooks';
import { Descendant } from 'slate';
import { getMessageThread } from '@tsClient';
import CommentMessage from '@components/Message/CommentMessage';
import PaginationObserver from '@components/PaginationObserver';

import { useIsUserInitialized } from '@providers/Auth';
import TeamsContext from '@providers/Teams/TeamsContext';
import ProgressIndicator from '@/components/ProgressIndicator';
import InputComposer from '@/components/InputComposer';
import Spacer from '@/components/Spacer';
import { ChatMessage } from '../ChatWithExpert/types';

type Props = {
  conversationSid: string;
  onSendReply: () => void;
  messageId: string;
  editMessageFn?: (data: {
    id: string;
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
    attachments?: ApiModels.CreateMessageAttachment[];
  }) => Promise<void>;
};

type Message = Parameters<typeof CommentMessage>[0]['comment'] &
  Pick<ApiModels.Message, 'id' | 'createdAt' | 'isDeleted' | 'text' | 'title' | 'type' | 'isPinned'> & {
    canReply?: boolean;
    canReplyForFree?: boolean;
    areCommentsHidden?: boolean;
    isContentHidden?: boolean;
    isAvailableForFree?: boolean;
    isAIGenerated?: boolean;
    owner?: {
      id: string;
      name?: string | null;
      image?: {
        url: string;
      } | null;
    } | null;
    tags: {
      id: string;
      name: string;
    }[];
    children?: Awaited<ReturnType<typeof getMessageThread>>['children'];
    medias: Pick<Types.Media, 'id' | 'vimeoUri' | 'type' | 's3OriginId' | 'url' | 'name'>[];
    viewPermissions: { id: string }[];
    commentPermissions: { id: string }[];
    replyPermissions: { id: string }[];
    product?: {
      creator: Pick<Types.Creator, 'id' | 'personId' | 'name'>;
    };
  };

const PAGE_SIZE = 50;

const ConversationThreadPage: FC<Props> = ({ conversationSid, messageId, onSendReply, editMessageFn }) => {
  const [user] = useUser();
  const { creatorId } = useContext(TeamsContext);
  const userId = user?.id;
  const userIsInitialized = useIsUserInitialized();
  const [message, setMessage] = useState<Message | null>(null);
  const { getData: getMessageThreadFn } = useNoodleApi(getMessageThread);
  const { data: creator, getData: getConversationCreatorFn } = useNoodleApi(
    tsClient.conversations.getConversationCreator,
  );
  const { getData: postConversationMessageFn } = useNoodleApi(tsClient.conversations.postConversationMessage, {
    healthMonitor: { name: 'send-message' },
  });
  const [children, setChildren] = useState<Awaited<ReturnType<typeof getMessageThread>>['children']>([]);
  const [totalChildren, setTotalChildren] = useState(0);
  const currentPageRef = useRef(1); // use ref instead of state to avoid potential races with multiple clicks.
  const [isInitializing, setIsInitializing] = useState(true);
  const [creatorPersonId, setCreatorPersonId] = useState<string | null>(null);
  const [creatorSlug, setCreatorSlug] = useState<string | null>(null);
  const [isPostingComment, setIsPostingComment] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<ChatMessage | null | undefined>(null);
  const hasCreatorPermissions = creatorId === creator?.id;

  const initializeMessage = async (): Promise<void> => {
    currentPageRef.current = 1;
    const messageResponse = await getMessageThreadFn({
      creatorSlug: 'not-required',
      messageId,
      page: currentPageRef.current,
      perPage: PAGE_SIZE,
      productSlug: 'not-required',
    });

    if (messageResponse.data) {
      const { children: newChildren, numChildren, ...newMessage } = messageResponse.data;
      setMessage(newMessage);
      setChildren(newChildren);
      setTotalChildren(numChildren);
    }
  };

  const fetchInitialData = async (): Promise<void> => {
    setIsInitializing(true);
    setMessage(null);
    setChildren([]);
    await initializeMessage();
    setIsInitializing(false);
  };

  useEffect(() => {
    const fetchCreatorData = async (): Promise<void> => {
      const { data: creatorData } = await getConversationCreatorFn({ conversationSid });
      setCreatorPersonId(creatorData?.personId || null);
      setCreatorSlug(creatorData?.slug || null);
    };
    fetchCreatorData();
  }, [conversationSid, getConversationCreatorFn]);

  useEffect(() => {
    if (userIsInitialized) {
      fetchInitialData();
    }
    // eslint-disable-next-line
  }, [userId, userIsInitialized, messageId, getMessageThreadFn]);

  if (!message || !creatorPersonId || !creatorSlug || isInitializing) {
    return (
      <ProgressIndicator isPage isCentered/>
    );
  }

  const handleLoadMore = async (): Promise<void> => {
    currentPageRef.current += 1;
    const messageResponse = await getMessageThreadFn({
      creatorSlug: 'not-required',
      messageId,
      page: currentPageRef.current,
      perPage: PAGE_SIZE,
      productSlug: 'not-required',
    });
    if (messageResponse.data) {
      const { children: newChildren, numChildren } = messageResponse.data;
      setChildren(previousChildren => {
        const forRemovingDuplicates = previousChildren.reduce<Record<string, boolean>>((acc, msg) => {
          acc[msg.id] = true;
          return acc;
        }, {});
        return [...previousChildren, ...newChildren.filter(msg => !forRemovingDuplicates[msg.id])];
      });
      setTotalChildren(numChildren);
    }
  };

  const refetchMessagesAfterReply = async ({ parentId, attachments, text, medias, handbooks }: {
    parentId: string;
    attachments?: ApiModels.CreateMessageAttachment[];
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
  }): Promise<void> => {
    if ((text?.length || medias?.length || handbooks?.length)) {
      setIsPostingComment(true);
      const newMessage = await postConversationMessageFn({
        attachments,
        conversationSid,
        handbooks,
        medias,
        messageId: parentId,
        messageType: ApiModels.MessageType.Chat,
        sendAsCreator: false,
        text: text ? { children: text } : null,
      });
      if (userIsInitialized && newMessage.data) {
        setChildren([
          {
            ...newMessage.data,
            children: [],
            medias: [],
            parent: null,
            products: [],
            rank: 0,
            upgradeTiers: [],
          },
          ...children,
        ]);
        onSendReply();
      }
      setIsPostingComment(false);
    }
  };

  const canFetchMore = children.length < totalChildren;

  const onSend = async ({
    text,
    attachments,
    medias,
  }: {
    text?: { children: Descendant[] }[];
    attachments?: ApiModels.CreateMessageAttachment[];
    medias?: { id: string }[];
  }): Promise<void> => {
    if (currentMessage) {
      if (editMessageFn) {
        await editMessageFn({
          attachments,
          id: currentMessage.id,
          medias,
          text,
        });
      }

      setCurrentMessage(null);

      await initializeMessage();
    } else {
      await refetchMessagesAfterReply({
        attachments,
        medias,
        parentId: message.id,
        text,
      });
    }
  };

  return (
    <div>
      <CommentMessage
        comment={{
          ...message,
          children,
        }}
        commentId={messageId}
        creatorPersonId={creatorPersonId}
        isThreadDetail
        creatorSlug={creatorSlug}
        refetchMessage={initializeMessage}
        canDeleteMessages={true}
        canPinMessages={false}
        canReplyToMessages={false}
        isConversationThread
        canEditMessages={true}
        setCurrentMessage={setCurrentMessage}
        currentMessage={{
          ...message,
          isAIGenerated: message.isAIGenerated ?? false,
          numChildren: message.numChildren ?? 0,
        }}
      />
      <Spacer size={16}/>
      <div>
        <InputComposer
          showAddVideo
          onSend={onSend}
          showProTips={false}
          isFetching={isPostingComment}
          placeholder={'Type your reply'}
          showAddHandbook={hasCreatorPermissions}
          isLoomEnabled={hasCreatorPermissions}
          referenceType={'comment-message'}
          referenceId={message.id}
          currentMessage={currentMessage}
        />
      </div>
      {!message.areCommentsHidden && canFetchMore && <PaginationObserver isDisabled={!canFetchMore} loadFn={handleLoadMore} />}
    </div>
  );
  // return null;
};

export default ConversationThreadPage;
